import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { doc, p } from '@atlaskit/adf-utils/builders';
import type { DocNode } from '@atlaskit/adf-schema';

import { AIAgentResponseState, useAIAgentStreaming } from '@atlassian/ai-agent-streaming';
import { adfToMarkdownPlus, markdownPlusToAdf } from '@atlassian/adf-markdown-transformer';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import { usePageContentId } from '@confluence/page-context';

import type { Issue, JiraAIRequest, JiraAIResponse } from '../__types__/AiAgentStreamingTypes';
import { type AiBulkIssuesListType } from '../providers/BulkCreateContextProvider';

import type {
	BulkIssueADFQuery as BulkIssueADFQueryType,
	BulkIssueADFQueryVariables,
} from './ADFtoMarkdownResources/__types__/BulkIssueADFQuery';
import { BulkIssueADFQuery } from './ADFtoMarkdownResources/BulkIssueADFQuery.graphql';

export const useAIAgentStreamingData = () => {
	const [completedIssues, setCompletedIssues] = useState<AiBulkIssuesListType>([]);
	const [markdownPlusContent, setMarkdownPlusContent] = useState('');
	const [pageIdMap, setPageIdMap] = useState<IDMap | undefined>(undefined);

	const [contentId] = usePageContentId();

	const pageId = contentId ?? '';

	// Used to keep track of the issues that have been added to the list and give a unique id
	const issueIndex = useRef(0);

	// Needed to convert confluence content into MD+ format for the ai request
	useQuery<BulkIssueADFQueryType, BulkIssueADFQueryVariables>(BulkIssueADFQuery, {
		variables: {
			contentId: pageId,
		},
		onError: (adfError) => {
			throw new Error('Error calling query', adfError);
		},
		fetchPolicy: 'network-only',
		onCompleted: (adfData) => {
			const adf = adfData?.singleContent?.body?.atlas_doc_format?.value;
			if (adf) {
				try {
					const parsedAdf: DocNode = adf ? JSON.parse(adf) : doc(p(''));
					const { markdown: markdownPlus, idMap } = adfToMarkdownPlus(parsedAdf);
					setMarkdownPlusContent(markdownPlus);
					setPageIdMap(idMap);
				} catch (err) {
					throw new Error('Failed to parse adf');
				}
			}
		},
	});

	const request: JiraAIRequest = {
		recipient_agent_named_id: 'ai_issue_create_agent',
		agent_input_context: {
			application: 'Confluence',
			context: {
				confluence_page_markdown_plus: markdownPlusContent,
				highlighted_text: null,
			},
			suggested_issues_config: {
				max_issues: 10,
				suggested_issue_field_types: [
					{
						issue_type: 'Task',
						fields: [
							{
								field_name: 'Summary',
								field_type: 'Short-Text',
							},
							{
								field_name: 'Description',
								field_type: 'Paragraph',
							},
						],
					},
				],
			},
		},
	};

	const skipDataStremaing = !markdownPlusContent;
	const [{ content, responseState }] = useAIAgentStreaming<JiraAIRequest, JiraAIResponse>(
		request,
		{
			enablePartialJson: true,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product': 'confluence',
			},
		},
		skipDataStremaing,
	);

	useEffect(() => {
		const getIssueDetails = () => {
			return {
				iconUrl: '',
				name: '',
				id: '',
			};
		};

		const isValidIssue = (issue: Issue) => {
			return issue?.field_values?.Summary && issue?.field_values?.Description;
		};

		// Only adds issues to the issues list if they are valid
		const addIssue = (issue: Issue) => {
			if (!isValidIssue(issue)) return;

			const newIssue = {
				id: String(issueIndex.current++),
				summary: issue.field_values.Summary,
				description: markdownPlusToAdf({
					markdown: issue.field_values.Description,
					idMap: pageIdMap,
				}),
				issueType: getIssueDetails(),
			};

			setCompletedIssues((currentIssues) => [...currentIssues, newIssue]);
		};

		// Switch statement to handle the different response states
		switch (responseState) {
			case AIAgentResponseState.Initialization:
				// Not needed really just want to track all the states
				setCompletedIssues([]);
				break;
			case AIAgentResponseState.AnswerPart:
			case AIAgentResponseState.FinalResponse:
				try {
					// No reason to try to add an issue if there isn't a new issue in the list
					if (
						content?.suggested_issues.length > 0 &&
						content.suggested_issues.length > issueIndex.current
					) {
						const issues = content.suggested_issues;
						addIssue(issues[issueIndex.current]);
					}
				} catch (error) {
					throw new Error('Failed to set completed issues', error);
				}
				break;
			case AIAgentResponseState.Error: {
				throw new Error('Failed to set completed issues');
			}
			default:
				break;
		}
	}, [content, pageIdMap, responseState, issueIndex]);

	return {
		content: completedIssues,
		responseState,
	};
};
